import React, { useCallback, useRef } from 'react';
import {
  Activity,
  AlertTriangle,
  MessageSquare,
  Printer,
  Trash2,
} from 'lucide-react';
import {
  Badge,
  Button,
  type VirtualizedFormSelectMenuHandle,
  Checkbox,
  cn,
  DatePicker,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type {
  AuthorizationBudget,
  AvailableClinicianResponse as AvailableClinician,
  DeletedTaskResponse,
  TaskListResponse,
  VisitTypeId,
} from '../../services/api/types.gen';
import type { EpisodeLupaPeriodResponse } from '../../services/api';
import VisitsCalendarPanel from '../VisitsCalendarPanel';
import TaskDocumentsPopover from '../documents/TaskDocumentsPopover';
import { ExternalOrderRenders } from '../orders/ExternalOrderRenders';
import { OutsideEpisodeBadge } from '../episode/OutsideEpisodeBadge';
import { PatientSignatureIcon } from '../visit/PatientSignatureIcon';
import { patientSignatureLabel } from '../visit/patientSignatureLabel';
import SelectionActionBar, {
  type SelectionAction,
} from '../SelectionActionBar';
import { LockedTaskIndicator } from '../task/LockedTaskIndicator';
import {
  getChartRowStatus,
  getStatusBadgeVariant,
  getStatusLabel,
} from '../task/taskStatus';
import {
  getScheduleLockedTask,
  isDocumentMutationLocked,
  type ScheduleLockedTask,
} from '../task/lockedTask';
import { canDeleteTask, taskManifestGroup } from '../task/batchActions';
import { RestoreTaskButton } from '../task/RestoreTaskButton';
import { TaskTypeFilter } from '../task/TaskTypeFilter';
import {
  isVisitTaskRow,
  type TaskTypeFilterValue,
} from '../task/taskTypeFilters';
import { ClinicianSelect } from '../clinician/ClinicianSelect';
import { UnclaimedClinicianName } from '../clinician/UnclaimedClinicianName';
import { unclaimedAssigneeMarker } from '../task/unclaimedAssignee';
import {
  isClosedEpisodeLocked,
  useCanAmendClosedEpisode,
} from '../../utils/closedEpisodeLock';
import { SchedulingPointLoadAnnotation } from '../scheduling/SchedulingPointLoadAnnotation';
import { supportsActiveDiagnosisPage } from '../../constants/visitTypes';
import { hasPrintView } from '../../hooks/usePrintView';
import type { TaskView } from '../../hooks/usePatientDetailTasks';
import type { ClinicianSelectInput } from '../../utils/clinicianUtils';
import {
  formatDateTimeInAgencyZone,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import {
  LOCKED_TASK_ASSIGNEE_TEXT_CLASS,
  LOCKED_TASK_DATE_TEXT_CLASS,
  TASK_GRID_LG,
  TASK_GRID_LG_WITH_EVV,
  TASK_TABLE_MIN_W_LG,
  TASK_TABLE_MIN_W_LG_WITH_EVV,
} from './patientDetailTaskConstants';

export interface TasksTabProps {
  patientId: string;
  /** "First Last", appended to a task's own label on its documents popover. */
  patientLabel?: string;
  effectiveSelectedEpisodeId: string | null;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;

  // Calendar
  calendarTasks: TaskListResponse[];
  assignableClinicians: AvailableClinician[];
  duplicateClinicianNames: Set<string>;
  lupaPeriods: EpisodeLupaPeriodResponse[];
  lupaLoading: boolean;
  authorizationBudget: AuthorizationBudget[];
  authorizationBudgetLoading: boolean;
  requiresAuthorization: boolean;
  unlinkedVisitCount: number;
  authorizationBudgetUnavailable: boolean;

  // Table rows and their states
  visitsLoading: boolean;
  visitsError: string | null;
  tasksForEpisode: TaskListResponse[];
  displayedTasks: TaskListResponse[];
  deletedTasks: DeletedTaskResponse[];
  deletedTaskById: Map<string, DeletedTaskResponse>;
  deletedTasksLoading: boolean;
  deletedTasksError: string | null;
  deletedTasksReady: boolean;
  taskView: TaskView;
  onTaskViewChange: (view: TaskView) => void;
  tableTaskTypeFilter: TaskTypeFilterValue;
  onTableTaskTypeFilterChange: (filter: TaskTypeFilterValue) => void;

  // Batch selection
  selectedTaskIds: Set<string>;
  selectedTasks: TaskListResponse[];
  selectAllState: boolean | 'indeterminate';
  atSelectionCap: boolean;
  onToggleTaskSelected: (taskId: string) => void;
  onToggleSelectAll: () => void;
  onClearTaskSelection: () => void;
  onRemoveTaskFromSelection: (task: TaskListResponse) => void;
  batchActions: SelectionAction<TaskListResponse>[];

  // Row rendering helpers owned by the page
  getTaskDisplayName: (task: TaskListResponse) => string | undefined;
  getTaskAssigneeLabel: (task: TaskListResponse) => string;
  getTaskAssigneeClinicians: (task: TaskListResponse) => ClinicianSelectInput[];
  formatDuplicateClinicianOptionLabel: (
    clinician: ClinicianSelectInput,
    label: string
  ) => string;
  /** Whether the page's identity and clinic boundary allow Episode Manager edits. */
  canEditEpisodeManagerTask: (task: TaskListResponse) => boolean;
  /** 30-Day Summaries stay inline-editable where other communications do not. */
  isThirtyDaySummaryTask: (taskId: string) => boolean;
  taskTargetDateBounds: (task: TaskListResponse) => {
    minDate: Date | undefined;
    maxDate: Date | undefined;
  };
  updatingTaskId: string | null;
  rejectedEdit: { taskId: string; nonce: number } | null;
  showEvvColumn: boolean;
  canViewSchedulingPointLoads: boolean;
  restoringTaskId: string | null;

  // Print
  printTask: (task: TaskListResponse) => void | Promise<void>;
  printingTaskId: string | null;
  printDeletedTask: (task: TaskListResponse) => void | Promise<void>;
  printingDeletedTaskId: string | null;

  // Callbacks back to the page
  onTaskOpen: (task: TaskListResponse) => void;
  onAssigneeChange: (taskId: string, clinicianId: string) => void;
  onTargetDateChange: (taskId: string, date: Date | undefined) => void;
  onCreateTaskFromDate: (date: Date) => void;
  onRequestDelete: (task: TaskListResponse) => void;
  onRestoreTask: (task: TaskListResponse) => void;
  onOpenEvv: (visitId: string) => void;
  onDocumentsChanged: () => void;
}

const formatDate = (dateString: string | null | undefined) =>
  formatDateToMMDDYYYY(dateString) || 'N/A';

/**
 * The chart's Tasks tab: the episode calendar, the task table (live, deleted or
 * both), the batch-selection bar and the row-level edit affordances.
 *
 * Owns no selection or episode state of its own — the page decides which
 * episode is on screen and which task is open, and every mutation here is a
 * callback back to it. The dialogs a row can trigger (delete confirm, EVV
 * readings, batch notes) stay on the page so a tab change cannot unmount them
 * mid-flight.
 */
export const TasksTab: React.FC<TasksTabProps> = ({
  patientId,
  patientLabel,
  effectiveSelectedEpisodeId,
  episodeStartDate,
  episodeEndDate,
  calendarTasks,
  assignableClinicians,
  duplicateClinicianNames,
  lupaPeriods,
  lupaLoading,
  authorizationBudget,
  authorizationBudgetLoading,
  requiresAuthorization,
  unlinkedVisitCount,
  authorizationBudgetUnavailable,
  visitsLoading,
  visitsError,
  tasksForEpisode,
  displayedTasks,
  deletedTasks,
  deletedTaskById,
  deletedTasksLoading,
  deletedTasksError,
  deletedTasksReady,
  taskView,
  onTaskViewChange,
  tableTaskTypeFilter,
  onTableTaskTypeFilterChange,
  selectedTaskIds,
  selectedTasks,
  selectAllState,
  atSelectionCap,
  onToggleTaskSelected,
  onToggleSelectAll,
  onClearTaskSelection,
  onRemoveTaskFromSelection,
  batchActions,
  getTaskDisplayName,
  getTaskAssigneeLabel,
  getTaskAssigneeClinicians,
  formatDuplicateClinicianOptionLabel,
  canEditEpisodeManagerTask,
  isThirtyDaySummaryTask,
  taskTargetDateBounds,
  updatingTaskId,
  rejectedEdit,
  showEvvColumn,
  canViewSchedulingPointLoads,
  restoringTaskId,
  printTask,
  printingTaskId,
  printDeletedTask,
  printingDeletedTaskId,
  onTaskOpen,
  onAssigneeChange,
  onTargetDateChange,
  onCreateTaskFromDate,
  onRequestDelete,
  onRestoreTask,
  onOpenEvv,
  onDocumentsChanged,
}) => {
  const showDeletedTasks = taskView !== 'active';
  // A closed period refuses a reassignment or reschedule unless the actor
  // holds the amend key (EMR-1859). The row hides the picker; the backend is
  // the gate.
  const canAmendClosedEpisode = useCanAmendClosedEpisode();
  // One picker per row, so the popover beside a row's unclaimed assignee can
  // open that row's list and no other.
  const assigneeMenuHandles = useRef(
    new Map<string, VirtualizedFormSelectMenuHandle>()
  );

  // EMR-325: the at-a-glance EVV state for one row. Only visits can carry EVV,
  // and the readings route was never widened for deleted visits, so both cases
  // render an empty cell that keeps the grid tracks aligned.
  const renderEvvIndicator = useCallback(
    (task: TaskListResponse, isDeletedRow: boolean) => {
      if (task.task_type !== 'visit' || isDeletedRow) {
        return <span aria-hidden='true' />;
      }
      // Not `has_evv_data` (true for a visit clocked in and never signed) and
      // not `evv_capture_state === 'complete'` (an unable-to-sign attestation
      // satisfies that too). Only `patient_signature` answers the question this
      // indicator claims to answer — the same rule Payroll Manager uses.
      const label = patientSignatureLabel(task.patient_signature);
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size='sm'
                aria-label={`${label} — open EVV readings for ${
                  getTaskDisplayName(task) || 'visit'
                }`}
                className='h-8 w-8 p-0 max-lg:h-7 max-lg:w-7'
                onClick={() => onOpenEvv(task.id)}
              >
                <PatientSignatureIcon state={task.patient_signature} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
    [getTaskDisplayName, onOpenEvv]
  );

  const renderStatusBadge = useCallback(
    (
      task: TaskListResponse,
      lockedTask: ScheduleLockedTask | null,
      className?: string,
      // The phone's status column fits about eleven characters, which is too
      // narrow for the missed-visit wording: it clips to "Sa..." and the two
      // finished states stop being distinguishable. The red Missed badge next
      // to the task name already says the visit was missed and stays legible,
      // so the narrow layout keeps the ordinary label and the full vocabulary
      // is left to the surfaces with room for it.
      withMissedWording = true
    ) => {
      const rowStatus = getChartRowStatus(
        task.status,
        task.signature_request_status,
        Boolean(task.missed_visit_data)
      );
      const statusLabel = getStatusLabel(
        rowStatus,
        withMissedWording ? task.missed_visit_data : null
      );
      return (
        <span className='inline-flex min-w-0 max-w-full items-center gap-1.5'>
          {lockedTask && (
            <span onClick={e => e.stopPropagation()}>
              <LockedTaskIndicator
                taskType={lockedTask.task_type}
                status={lockedTask.status}
              />
            </span>
          )}
          <Badge
            variant={getStatusBadgeVariant(rowStatus)}
            className={cn('max-w-full', className)}
            title={statusLabel}
          >
            <span className='truncate'>{statusLabel}</span>
          </Badge>
        </span>
      );
    },
    []
  );

  // Bottom-of-table switch between the episode's live tasks and its deleted
  // ones. Centered and always rendered, including under an empty live list,
  // because "there is nothing here" is exactly when someone goes looking for
  // what was removed.
  const deletedTasksToggle = (
    <div className='mt-3 flex justify-center max-lg:mt-2'>
      <Tabs
        value={taskView}
        variant='purple'
        onValueChange={value => onTaskViewChange(value as TaskView)}
      >
        <TabsList>
          {/*
            Wrap each phrase so " tasks" stays in the same inline box.
            TabsTrigger is inline-flex; a leading space on a sibling flex
            item collapses, which rendered "Activetasks" on lg+ (EMR-1618).
          */}
          <TabsTrigger value='active'>
            <span>
              Active<span className='max-lg:sr-only'> tasks</span>
            </span>
          </TabsTrigger>
          <TabsTrigger value='deleted'>
            <Trash2 className='mr-1.5 h-3.5 w-3.5 max-lg:mr-1' />
            <span>
              Deleted<span className='max-lg:sr-only'> tasks</span>
            </span>
          </TabsTrigger>
          <TabsTrigger value='all'>
            <span>
              All<span className='max-lg:sr-only'> tasks</span>
            </span>
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );

  return (
    <>
      <VisitsCalendarPanel
        key={effectiveSelectedEpisodeId ?? 'none'}
        visits={calendarTasks}
        episodeStartDate={episodeStartDate ?? undefined}
        episodeEndDate={episodeEndDate ?? undefined}
        onVisitClick={onTaskOpen}
        onCreateTaskFromDate={onCreateTaskFromDate}
        clinicians={assignableClinicians}
        duplicateClinicianNames={duplicateClinicianNames}
        lupaPeriods={lupaPeriods}
        lupaLoading={lupaLoading}
        authorizationBudget={authorizationBudget}
        authorizationBudgetLoading={authorizationBudgetLoading}
        requiresAuthorization={requiresAuthorization}
        unlinkedVisitCount={unlinkedVisitCount}
        authorizationBudgetUnavailable={authorizationBudgetUnavailable}
      />
      <div className='mt-4 pt-4 max-lg:mt-2 max-lg:pt-2'>
        {visitsLoading ? (
          <div className='text-center py-8'>
            <div className='flex justify-center mb-4'>
              <span className='flex items-center gap-1'>
                <div className='animate-spin rounded-full border-b-2 border-primary h-8 w-8' />
              </span>
            </div>
            <p className='text-muted-foreground'>Loading tasks...</p>
          </div>
        ) : visitsError ? (
          <div className='py-8 text-center text-muted-foreground'>
            <AlertTriangle className='h-12 w-12 mx-auto mb-4 opacity-50' />
            <p>{visitsError}</p>
          </div>
        ) : !showDeletedTasks && tasksForEpisode.length === 0 ? (
          <>
            <div className='py-8 text-center text-muted-foreground'>
              <Activity className='h-12 w-12 mx-auto mb-4 opacity-50' />
              <p>No tasks found for this episode</p>
            </div>
            {deletedTasksToggle}
          </>
        ) : (
          <>
            <div className='overflow-hidden rounded-md border border-border/60 bg-background/40'>
              <TaskTypeFilter
                variant='inline'
                value={tableTaskTypeFilter}
                onChange={onTableTaskTypeFilterChange}
                // Counted from the same rows the table paints, so
                // the "all" view's denominator covers live and
                // deleted together rather than deleted alone.
                totalCount={
                  taskView === 'active' ||
                  (taskView === 'all' && !deletedTasksReady)
                    ? tasksForEpisode.length
                    : !deletedTasksReady
                      ? 0
                      : taskView === 'deleted'
                        ? deletedTasks.length
                        : tasksForEpisode.length + deletedTasks.length
                }
                filteredCount={displayedTasks.length}
              />
              {tableTaskTypeFilter === 'orders' && patientId && (
                <div className='px-3 pb-3'>
                  <ExternalOrderRenders
                    patientId={patientId}
                    episodeId={effectiveSelectedEpisodeId}
                  />
                </div>
              )}
              {/* The shell above keeps overflow-hidden for its
                rounded corners, so the table needs its own
                scroller: at lg the grid's track minimums exceed a
                laptop's content width once the sidebar is open,
                and without this wrapper the EVV and Actions
                columns were clipped with no way to reach them. */}
              <div
                data-testid='episode-task-table-scroll'
                className='overflow-x-auto'
              >
                <div
                  className={`hidden lg:grid ${
                    showEvvColumn ? TASK_GRID_LG_WITH_EVV : TASK_GRID_LG
                  } ${
                    showEvvColumn
                      ? TASK_TABLE_MIN_W_LG_WITH_EVV
                      : TASK_TABLE_MIN_W_LG
                  } items-center gap-3 border-b border-border/60 bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground`}
                >
                  {/* Nothing in the deleted-only view is selectable,
                  so the header cell is empty there. The mixed view
                  keeps it: it selects the live rows, which are the
                  ones the batch actions can act on. The empty cell
                  keeps the columns aligned with the rows. */}
                  {taskView === 'deleted' ? (
                    <span />
                  ) : (
                    <Checkbox
                      checkboxSize='sm'
                      aria-label='Select all tasks'
                      checked={selectAllState}
                      onCheckedChange={() => onToggleSelectAll()}
                    />
                  )}
                  <span>Task</span>
                  <span>Assignee</span>
                  <span>Visit Date</span>
                  <span>Target Date</span>
                  <span>Status</span>
                  {showEvvColumn && <span className='text-center'>Pt Sig</span>}
                  <span className='text-center'>Actions</span>
                </div>
                {/* The mixed view's own notice, above the branch
                chain rather than inside its last arm: an episode
                whose live list is empty still has to be told the
                deleted list failed, and that arm never renders. */}
                {taskView === 'all' &&
                  (deletedTasksError ? (
                    <div
                      role='alert'
                      className='px-3 py-2 text-xs text-destructive'
                    >
                      {deletedTasksError} Showing active tasks only.
                    </div>
                  ) : deletedTasksLoading || !deletedTasksReady ? (
                    <div className='px-3 py-2 text-xs text-muted-foreground'>
                      Loading deleted tasks...
                    </div>
                  ) : null)}
                {/* These three states belong to the deleted-only
                view, which has nothing else to show. The mixed view
                keeps painting its live half and reports the deleted
                list's state in the notice above: a chart list that
                loaded fine should not be taken down by a second
                list that did not. */}
                {taskView === 'deleted' &&
                (deletedTasksLoading || !deletedTasksReady) ? (
                  <div className='py-8 text-center text-muted-foreground'>
                    Loading deleted tasks...
                  </div>
                ) : taskView === 'deleted' && deletedTasksError ? (
                  <div
                    role='alert'
                    className='py-8 text-center text-destructive'
                  >
                    {deletedTasksError}
                  </div>
                ) : taskView === 'deleted' &&
                  (!effectiveSelectedEpisodeId || deletedTasks.length === 0) ? (
                  <div className='py-8 text-center text-muted-foreground'>
                    {effectiveSelectedEpisodeId
                      ? 'No deleted tasks for this episode.'
                      : 'No episode selected, so there are no deleted tasks to show.'}
                  </div>
                ) : displayedTasks.length === 0 ? (
                  <div className='py-8 text-center text-muted-foreground'>
                    {/* Blaming the filters for an episode that simply
                    has no tasks sends the user to change a filter
                    that is not hiding anything. */}
                    {tasksForEpisode.length + deletedTasks.length === 0
                      ? 'No tasks for this episode.'
                      : 'No tasks match these filters.'}
                  </div>
                ) : (
                  <div
                    className={`divide-y divide-border/60 ${
                      showEvvColumn
                        ? TASK_TABLE_MIN_W_LG_WITH_EVV
                        : TASK_TABLE_MIN_W_LG
                    }`}
                  >
                    {displayedTasks.map(task => {
                      // A deleted row is strictly read only: every
                      // affordance below that would POST or PATCH is
                      // gated on this, not merely hidden by layout.
                      // Per row, not per view: "all" interleaves live
                      // and deleted tasks, so the view alone cannot
                      // say whether this one is deleted.
                      const deletedRow = deletedTaskById.get(task.id);
                      const isDeletedRow = deletedRow !== undefined;
                      const lockedTask = getScheduleLockedTask(task);
                      const isLockedTask = lockedTask !== null;
                      const isCommTask =
                        task.task_type === 'patient_communication';
                      // 30-Day Summaries are assigned from this table
                      // (the detail page no longer carries an assignee
                      // picker), so allow inline assignee editing for
                      // them even though other comms stay read-only.
                      const isThirtyDaySummaryRow =
                        isCommTask && isThirtyDaySummaryTask(task.id);
                      // Only a visit's move or reassignment is refused on
                      // a closed period; an order, task or communication
                      // has no such gate behind it, so its row keeps its
                      // pickers.
                      const closedEpisodeApplies =
                        task.task_type === 'visit' &&
                        task.episode_closed === true;
                      const closedEpisodeLocked = isClosedEpisodeLocked(
                        closedEpisodeApplies,
                        canAmendClosedEpisode
                      );
                      const canEditEpisodeManager =
                        canEditEpisodeManagerTask(task);
                      const canEditAssigneeInline =
                        !isDeletedRow &&
                        !isLockedTask &&
                        !closedEpisodeLocked &&
                        canEditEpisodeManager &&
                        (!isCommTask || isThirtyDaySummaryRow);
                      // 30-Day Summaries expose an editable target date
                      // from this table, matching Visits/Orders, since
                      // the episode start date can shift by a few days.
                      // Other communications stay read-only here.
                      const canEditDateInline =
                        !isDeletedRow &&
                        !isLockedTask &&
                        !closedEpisodeLocked &&
                        canEditEpisodeManager &&
                        (!isCommTask || isThirtyDaySummaryRow);
                      const canDelete = !isDeletedRow && canDeleteTask(task);
                      // An unclaimed imported assignee is explained by a
                      // marker on the name. The marker offers a reassignment
                      // only where this row would accept one.
                      const assigneeUnclaimed =
                        task.assigned_clinician_unclaimed_import === true;
                      const assigneeMarker = assigneeUnclaimed
                        ? unclaimedAssigneeMarker({
                            taskType: task.task_type,
                            canEditInline: canEditAssigneeInline,
                            deleted: isDeletedRow,
                            episodeClosed: closedEpisodeApplies,
                            canAmendClosedEpisode,
                            lockedByStatus: isLockedTask,
                            episodeManagerDenied: !canEditEpisodeManager,
                          })
                        : null;
                      const reassignAction =
                        assigneeMarker?.variant === 'reassignable' ? (
                          <Button
                            type='button'
                            size='sm'
                            variant='outline'
                            onClick={() =>
                              assigneeMenuHandles.current
                                .get(task.id)
                                ?.openMenu()
                            }
                          >
                            Reassign
                          </Button>
                        ) : undefined;
                      const targetDateBounds = taskTargetDateBounds(task);

                      return (
                        <div
                          key={task.id}
                          data-testid={`task-row-${task.id}`}
                          // A deleted row is tinted and edged in the
                          // destructive colour so it cannot be
                          // mistaken for a live task at a glance. The
                          // caption alone reads as ordinary metadata.
                          className={`group cursor-pointer px-3 py-2 transition-colors max-lg:px-2 max-lg:py-1 lg:py-2.5 ${
                            isDeletedRow
                              ? 'border-l-2 border-destructive/70 bg-destructive/5 hover:bg-destructive/10'
                              : 'hover:bg-muted/35'
                          }`}
                          onClick={() => onTaskOpen(task)}
                        >
                          <div
                            className={`grid gap-y-1.5 max-lg:grid-cols-[auto_minmax(0,1fr)_auto] max-lg:items-center max-lg:gap-x-1.5 max-lg:gap-y-1 ${
                              showEvvColumn
                                ? TASK_GRID_LG_WITH_EVV
                                : TASK_GRID_LG
                            } lg:items-center lg:gap-x-3`}
                          >
                            <div
                              className='flex items-center'
                              onClick={e => e.stopPropagation()}
                            >
                              {!isDeletedRow && (
                                <Checkbox
                                  checkboxSize='sm'
                                  aria-label={`Select ${getTaskDisplayName(task)}`}
                                  checked={selectedTaskIds.has(task.id)}
                                  onCheckedChange={() =>
                                    onToggleTaskSelected(task.id)
                                  }
                                  disabled={
                                    !selectedTaskIds.has(task.id) &&
                                    atSelectionCap
                                  }
                                />
                              )}
                            </div>
                            <div className='min-w-0'>
                              <div className='flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground'>
                                <span className='min-w-0 truncate'>
                                  {getTaskDisplayName(task)}
                                </span>
                                {task.missed_visit_data && (
                                  <Badge
                                    variant='destructive'
                                    className='shrink-0 text-[10px] px-1.5 py-0'
                                  >
                                    Missed
                                  </Badge>
                                )}
                                {isVisitTaskRow(task) &&
                                  task.visit_type &&
                                  supportsActiveDiagnosisPage(
                                    task.visit_type as VisitTypeId
                                  ) &&
                                  task.has_active_diagnosis && (
                                    <Badge
                                      variant='secondary'
                                      className='shrink-0 text-[10px] px-1.5 py-0'
                                      title='Active diagnosis entered'
                                    >
                                      Dx
                                    </Badge>
                                  )}
                                {/* The Target Date column shows the
                                scheduled date for visits and the
                                task date for everything else. */}
                                <OutsideEpisodeBadge
                                  episodeStartDate={task.episode_start_date}
                                  episodeEndDate={task.episode_end_date}
                                  targetDate={
                                    isVisitTaskRow(task)
                                      ? task.scheduled_date
                                      : task.task_date
                                  }
                                  visitDate={
                                    isVisitTaskRow(task)
                                      ? task.task_date
                                      : undefined
                                  }
                                />
                                {/* Shrinkable rather than fixed, so a long
                                label clips instead of eating the task name.
                                Clipping needs a constrained chain all the way
                                down, which is why renderStatusBadge's own span
                                is bounded too. */}
                                <span className='min-w-0 lg:hidden'>
                                  {renderStatusBadge(
                                    task,
                                    lockedTask,
                                    'text-[10px]',
                                    false
                                  )}
                                </span>
                              </div>
                              {/* Who removed the record and when.
                              The one thing a deleted row carries
                              that a live one does not, so it
                              lives on the row rather than behind
                              a disclosure. */}
                              {deletedRow && (
                                <div className='mt-0.5 text-xs font-medium text-destructive'>
                                  Deleted{' '}
                                  {formatDateTimeInAgencyZone(
                                    deletedRow.deleted_at
                                  ) || 'Unknown'}
                                  {deletedRow.deleted_by_name
                                    ? ` by ${deletedRow.deleted_by_name}`
                                    : ''}
                                </div>
                              )}
                            </div>
                            {/* Mobile: assignee, visit, and target
                            share one labeled row. Desktop
                            (`lg:contents`) still feeds Assignee /
                            Visit / Target as separate table
                            columns. */}
                            <div className='col-span-3 grid min-w-0 grid-cols-[minmax(0,1fr)_max-content_max-content] items-center gap-x-1 lg:contents'>
                              <div
                                onClick={
                                  canEditAssigneeInline
                                    ? e => e.stopPropagation()
                                    : undefined
                                }
                                className='min-w-0 lg:block lg:space-y-1'
                              >
                                <span className='sr-only lg:hidden'>
                                  Assignee
                                </span>
                                {!canEditAssigneeInline ? (
                                  <div
                                    className={cn(
                                      LOCKED_TASK_ASSIGNEE_TEXT_CLASS,
                                      'max-lg:h-6 max-lg:truncate max-lg:px-1 max-lg:text-[11px]'
                                    )}
                                  >
                                    <span className='truncate'>
                                      <UnclaimedClinicianName
                                        name={getTaskAssigneeLabel(task)}
                                        unclaimed={assigneeUnclaimed}
                                        variant={assigneeMarker?.variant}
                                        detailLines={
                                          assigneeMarker?.detailLines
                                        }
                                        testId='task-assignee-unclaimed'
                                      />
                                    </span>
                                  </div>
                                ) : (
                                  <ClinicianSelect
                                    label=''
                                    name={`task-assignee-${task.id}`}
                                    selectSize='sm'
                                    placeholder='Unassigned'
                                    className='min-w-0'
                                    clinicians={getTaskAssigneeClinicians(task)}
                                    value={task.assigned_clinician_id || ''}
                                    onChange={event => {
                                      const value = event.target.value;
                                      if (
                                        !value &&
                                        task.assigned_clinician_id
                                      ) {
                                        return;
                                      }
                                      // The unclaimed assignee is in
                                      // this list to be shown, not to
                                      // be chosen. Picking them again
                                      // changes nothing, so it saves
                                      // nothing.
                                      if (
                                        value === task.assigned_clinician_id &&
                                        task.assigned_clinician_unclaimed_import ===
                                          true
                                      ) {
                                        return;
                                      }
                                      onAssigneeChange(task.id, value);
                                    }}
                                    disabled={updatingTaskId === task.id}
                                    isClearable={false}
                                    optionLabelFormatter={
                                      formatDuplicateClinicianOptionLabel
                                    }
                                    // One picker per task row, so the
                                    // combobox needs an id of its own
                                    // to be addressable. `name` lands
                                    // on the hidden value input
                                    // instead.
                                    inputId={`task-assignee-${task.id}`}
                                    menuRef={handle => {
                                      if (handle) {
                                        assigneeMenuHandles.current.set(
                                          task.id,
                                          handle
                                        );
                                      } else {
                                        assigneeMenuHandles.current.delete(
                                          task.id
                                        );
                                      }
                                    }}
                                    // react-select builds option rows
                                    // only while the menu is open, and
                                    // renders the closed control
                                    // through the same callback with
                                    // context 'value'. So the load
                                    // badge cannot follow the
                                    // selection into the field, and
                                    // nothing is
                                    // fetched until someone actually
                                    // opens the list — both of which
                                    // the Radix path next to this one
                                    // has to arrange by hand.
                                    //
                                    // `scheduled_date`, not
                                    // `task_date`: the latter is the
                                    // display date (`visit_date`) and
                                    // can diverge once a visit is
                                    // completed, while the week is
                                    // bucketed by the plan's own date.
                                    formatOptionLabel={(
                                      option,
                                      { context }
                                    ) => {
                                      // Only the current assignee can
                                      // be unclaimed here — the list
                                      // holds no other.
                                      const unclaimed =
                                        assigneeUnclaimed &&
                                        option.value ===
                                          task.assigned_clinician_id;
                                      // The closed control carries the
                                      // Reassign action; a row in the
                                      // open list is already the act.
                                      const name = (
                                        <UnclaimedClinicianName
                                          name={option.label}
                                          unclaimed={unclaimed}
                                          variant={assigneeMarker?.variant}
                                          detailLines={
                                            assigneeMarker?.detailLines
                                          }
                                          action={
                                            context === 'value'
                                              ? reassignAction
                                              : undefined
                                          }
                                          testId='task-assignee-select-unclaimed'
                                        />
                                      );
                                      return context === 'menu' ? (
                                        <span className='flex w-full items-center justify-between gap-3'>
                                          <span className='truncate'>
                                            {name}
                                          </span>
                                          <SchedulingPointLoadAnnotation
                                            clinicId={task.agency_clinic_id}
                                            date={
                                              task.scheduled_date ??
                                              task.task_date
                                            }
                                            userId={option.user_id}
                                            enabled={
                                              canViewSchedulingPointLoads
                                            }
                                          />
                                        </span>
                                      ) : unclaimed ? (
                                        // The closed control is inside
                                        // react-select's own mousedown
                                        // target, which opens the menu.
                                        // The marker holds its badge's
                                        // and popover's mousedowns back,
                                        // so the explanation opens
                                        // instead; clicking the name
                                        // still opens the list.
                                        <span className='min-w-0'>{name}</span>
                                      ) : (
                                        option.label
                                      );
                                    }}
                                  />
                                )}
                              </div>
                              <div className='flex min-w-0 items-center gap-0.5 lg:block lg:space-y-1'>
                                <span className='shrink-0 text-[9px] font-medium leading-none text-muted-foreground lg:hidden'>
                                  Visit
                                </span>
                                {!isVisitTaskRow(task) ? (
                                  <span className='block text-sm text-muted-foreground max-lg:text-[11px]'>
                                    —
                                  </span>
                                ) : (
                                  <span
                                    className={cn(
                                      LOCKED_TASK_DATE_TEXT_CLASS,
                                      'max-lg:h-auto max-lg:max-w-none max-lg:pr-0 max-lg:text-[11px]'
                                    )}
                                  >
                                    {formatDate(task.task_date)}
                                  </span>
                                )}
                              </div>
                              <div
                                onClick={
                                  canEditDateInline
                                    ? e => e.stopPropagation()
                                    : undefined
                                }
                                className='flex min-w-0 items-center gap-0.5 lg:block lg:space-y-1'
                              >
                                <span className='shrink-0 text-[9px] font-medium leading-none text-muted-foreground lg:hidden'>
                                  Target
                                </span>
                                {!canEditDateInline ? (
                                  <span
                                    className={
                                      isLockedTask
                                        ? cn(
                                            LOCKED_TASK_DATE_TEXT_CLASS,
                                            'max-lg:h-auto max-lg:max-w-none max-lg:pr-0 max-lg:text-[11px]'
                                          )
                                        : 'block text-sm text-foreground max-lg:text-[11px]'
                                    }
                                  >
                                    {formatDate(
                                      isVisitTaskRow(task)
                                        ? task.scheduled_date
                                        : task.task_date
                                    )}
                                  </span>
                                ) : (
                                  <DatePicker
                                    key={`target-date-${task.id}-${
                                      rejectedEdit?.taskId === task.id
                                        ? rejectedEdit.nonce
                                        : 0
                                    }`}
                                    id={`task-target-date-${task.id}`}
                                    date={
                                      (
                                        isVisitTaskRow(task)
                                          ? task.scheduled_date
                                          : task.task_date
                                      )
                                        ? parseISOToLocalDate(
                                            isVisitTaskRow(task)
                                              ? task.scheduled_date
                                              : task.task_date
                                          )
                                        : undefined
                                    }
                                    onDateChange={(date: Date | undefined) =>
                                      onTargetDateChange(task.id, date)
                                    }
                                    disabled={updatingTaskId === task.id}
                                    datePickerSize='sm'
                                    className='w-full max-w-[220px] max-lg:w-[6.75rem] max-lg:max-w-[6.75rem]'
                                    minDate={targetDateBounds.minDate}
                                    maxDate={targetDateBounds.maxDate}
                                  />
                                )}
                              </div>
                            </div>
                            <div className='hidden lg:block'>
                              {renderStatusBadge(task, lockedTask, 'text-xs')}
                            </div>
                            {/* Desktop: its own track, so the pins
                            line up into a scannable column. The
                            actions cluster already owns
                            col-start-3/row-start-1 on mobile, and a
                            second item placed there would paint on
                            top of it — so below lg the indicator
                            joins that cluster instead (see below)
                            rather than claiming the same cell. */}
                            {showEvvColumn && (
                              <div
                                className='hidden lg:flex lg:items-center lg:justify-center'
                                onClick={e => e.stopPropagation()}
                              >
                                {renderEvvIndicator(task, isDeletedRow)}
                              </div>
                            )}
                            <div className='flex items-center justify-center gap-1 max-lg:col-start-3 max-lg:row-start-1 max-lg:justify-end max-lg:gap-0'>
                              {showEvvColumn && (
                                <div
                                  className='lg:hidden'
                                  onClick={e => e.stopPropagation()}
                                >
                                  {renderEvvIndicator(task, isDeletedRow)}
                                </div>
                              )}
                              {task.notes ? (
                                <div onClick={e => e.stopPropagation()}>
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button className='rounded p-1 hover:bg-muted max-lg:p-0.5'>
                                        <MessageSquare className='h-4 w-4 text-blue-500' />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className='max-w-sm'>
                                      <p className='whitespace-pre-wrap text-sm'>
                                        {task.notes}
                                      </p>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                              ) : null}
                              <div onClick={e => e.stopPropagation()}>
                                <TaskDocumentsPopover
                                  taskId={task.id}
                                  patientId={patientId}
                                  initialCount={task.document_count}
                                  includeDeleted={isDeletedRow}
                                  readOnly={
                                    isDeletedRow ||
                                    isDocumentMutationLocked(task)
                                  }
                                  targetLabel={`${
                                    getTaskDisplayName(task) || 'Task'
                                  }${patientLabel ? ` · ${patientLabel}` : ''}`}
                                  onDocumentsChanged={onDocumentsChanged}
                                />
                              </div>
                              {hasPrintView(task) && (
                                <div onClick={e => e.stopPropagation()}>
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    aria-label={`Print view for ${getTaskDisplayName(
                                      task
                                    )}`}
                                    disabled={
                                      (isDeletedRow
                                        ? printingDeletedTaskId
                                        : printingTaskId) === task.id
                                    }
                                    onClick={() =>
                                      void (
                                        isDeletedRow
                                          ? printDeletedTask
                                          : printTask
                                      )(task)
                                    }
                                    className='h-8 w-8 p-0 max-lg:h-7 max-lg:w-7'
                                  >
                                    {(isDeletedRow
                                      ? printingDeletedTaskId
                                      : printingTaskId) === task.id ? (
                                      <div className='animate-spin rounded-full h-3 w-3 border-b-2 border-current' />
                                    ) : (
                                      <Printer className='h-4 w-4' />
                                    )}
                                  </Button>
                                </div>
                              )}
                              {isDeletedRow && (
                                <div onClick={e => e.stopPropagation()}>
                                  <RestoreTaskButton
                                    compact
                                    task={task}
                                    taskLabel={
                                      getTaskDisplayName(task) || 'task'
                                    }
                                    restoring={restoringTaskId === task.id}
                                    onRestore={() => onRestoreTask(task)}
                                  />
                                </div>
                              )}
                              {canDelete && (
                                <div onClick={e => e.stopPropagation()}>
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    aria-label={`Delete ${getTaskDisplayName(
                                      task
                                    )}`}
                                    onClick={() => onRequestDelete(task)}
                                    disabled={updatingTaskId === task.id}
                                    className='h-8 w-8 p-0 opacity-70 transition-opacity group-hover:opacity-100 max-lg:h-7 max-lg:w-7 max-lg:opacity-100'
                                  >
                                    <Trash2 className='h-4 w-4 text-destructive' />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Every batch action mutates, so a deleted row
                offers no selection to feed them and the
                deleted-only view has no bar at all. The mixed view
                keeps it, because its live rows are selectable and a
                selection with no action bar would tick and do
                nothing. */}
              {taskView !== 'deleted' && (
                <SelectionActionBar
                  items={selectedTasks}
                  onClear={onClearTaskSelection}
                  onRemoveItem={onRemoveTaskFromSelection}
                  actions={batchActions}
                  getItemKey={task => task.id}
                  getItemLabel={task => getTaskDisplayName(task) || 'Task'}
                  getItemGroup={taskManifestGroup}
                />
              )}
            </div>
            {deletedTasksToggle}
          </>
        )}
      </div>
    </>
  );
};

export default TasksTab;
